import React, { useContext } from 'react';

import assetModalContext from '@components/asset/modal/tabs/edit/asset_modal_context';

import UppyUploader from '@components/common/uppy/main';
import FileStackUploader from '@components/common/filestack_uploader/main';

import {
  ActionTypes,
  ThumbnailOverrideDispatch,
  ThumbnailOverridePayloadOperations
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { LinkObject } from '@api/v4/assets/assetTypes';
import { UppyFile } from '@components/common/uppy/UppyTypes';
import { FileStackOnUpload } from '@/components/common/filestack_uploader/FileStackTypes';


interface ThumbnailOverrideScopedContext {
  state: {
    editState: {
      thumbnailOverride: LinkObject;
    };
  };
  dispatch: ThumbnailOverrideDispatch;
}

const ThumbnailOverrideComponent = (): React.ReactElement => {
  const { state, dispatch }: ThumbnailOverrideScopedContext = useContext(assetModalContext);
  const { thumbnailOverride } = state.editState;
  const uploadAreaId = "thumbnail-override-trigger-thumbnail-override";
  const deleteAssetThumbnailOverride = (): void => {
    const payload = {
      operation: ThumbnailOverridePayloadOperations.Delete
    };
    dispatch({ type: ActionTypes.ThumbnailOverrideAction, payload });
  };

  const updateAssetThumbnailOverrideUppy = (files: UppyFile[]): void => {
    if (files.length === 0) return;
    const payload = {
      operation: ThumbnailOverridePayloadOperations.Update,
      url: files[0].url,
      filename: files[0].name
    };
    dispatch({ type: ActionTypes.ThumbnailOverrideAction, payload });
  };

  const updateAssetThumbnailOverrideFileStack = (files: FileStackOnUpload): void => {
    if (files.filesUploaded.length === 0) return;
    const payload = {
      operation: ThumbnailOverridePayloadOperations.Update,
      url: files.filesUploaded[0].url,
      filename: files.filesUploaded[0].filename
    };
    dispatch({ type: ActionTypes.ThumbnailOverrideAction, payload });
  };

  const onError = (): void => {
    Notify.create({ type: 'error', title: t("Error Uploading Files") });
  };

  const uploadProvider = (): React.ReactElement => (
    <>
      {
        BFG.hasFeature('uppy_uploader_features') ? (
          <>
            <div
              id={uploadAreaId}
              className="uppy-thumbnail-override-settings"
            >
              {t("Click to upload")}
            </div>
            <UppyUploader
              button
              handleUpload={updateAssetThumbnailOverrideUppy}
              restrictions={{ maxNumberOfFiles: 1, allowedFileTypes: ['image/*'] }}
              template="thumbnail-override"
              trigger={`#${uploadAreaId}`}
              uniqIdentifier="uppy-thumbnail-override-thumbnail-override"
              uploadType="thumbnail-override"
            />
          </>
        ) : (
          <div
            id={uploadAreaId}
          >
            <FileStackUploader
              dropzoneContainerId={uploadAreaId}
              onError={onError}
              onSuccess={updateAssetThumbnailOverrideFileStack}
              options={
                {
                  accept: ["image/png", "image/jpg", "image/jpeg", "image/gif"],
                  maxFiles: 1
                }
              }
              type="area"
            />
          </div>
        )
      }
    </>
  );

  return (
    <div className="asset-advanced__feature">
      <span>{t("Custom Asset Thumbnail")}</span>
      <div className="thumbnail-override-upload-area">
        {
          (thumbnailOverride.url && thumbnailOverride.filename) ? (
            <div className="asset-advanced__feature--upload-complete">
              <a
                className="asset-advanced__feature--link filename"
                href={thumbnailOverride.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {thumbnailOverride.filename}
              </a>
              <span
                role="button"
                tabIndex={0}
                className="bff-trash remove-thumbnail-override"
                title="Remove Custom Asset Thumbnail"
                onClick={(): void => deleteAssetThumbnailOverride()}
                onKeyPress={(): void => deleteAssetThumbnailOverride()}
              />
            </div>
          ) : uploadProvider()
        }
      </div>
    </div>
  );
};

export default ThumbnailOverrideComponent;
